import { DataCollection } from "@dhx/ts-data";
import { AxisCreator } from "../AxisCreator";
import { IAxisLike, IGridRenderConfig, ILikeSeria, IScale, IScaleConfig, Locator, ScaleType, SvgElement } from "../types";
import { bottom, bottomGrid, left, leftGrid, right, rightGrid, top, topGrid } from "./SvgScales";

import {locator, log10} from "../helpers/common";

const renderScale = {
	left,
	right,
	bottom,
	top
};
const renderGrid = {
	left: leftGrid,
	right: rightGrid,
	bottom: bottomGrid,
	top: topGrid
};

export class Scale implements IScale {
	public locator: Locator;

	protected _isXDirection: boolean;
	protected config: IScaleConfig;
	protected _axis: IAxisLike<any>;
	protected _position: ScaleType;
	protected _padding: boolean = false;

	protected _charts: ILikeSeria[] = [];

	constructor(protected _data: DataCollection, config: IScaleConfig, position: ScaleType) {
		this._position = position;
		this._setDefaults(config);
		this._isXDirection = position === ScaleType.bottom || position === ScaleType.top;

		if (position !== ScaleType.radial){
			if (position === ScaleType.left || position === ScaleType.right){
				this.config.size = this.config.size || 40 + (this.config.title ? 40 : 0);
			} else {
				this.config.size = this.config.size || 20 + (this.config.title ? 40 : 0);
			}
		}
	}

	addPadding() {
		this._padding = true;
	}

	getSize():number{
		return this.config.size;
	}

	scaleReady(sizes): void {
		const points = [];
		this._charts.forEach(chart => {
			chart.getPoints().forEach(item => points.push(item[1])); // y-value
		});

		this._axis = new AxisCreator(points, this.config).getScale();

		const position = this._position;
		if (position !== "radial"){
			sizes[position] += this.config.size;
		}
	}
	point(pos: number): number {
		if (this.config.log) {
			return this._logPoint(pos);
		} else {
			return this._isXDirection ? (pos - this._axis.min)  / (this._axis.max - this._axis.min) : 1 - (pos - this._axis.min) / (this._axis.max - this._axis.min);
		}
	}
	add(val: ILikeSeria): void {
		this._charts.push(val);
	}
	paint(width: number, height: number): SvgElement {
		if (this.config.hidden){
			return null;
		}

		const steps = this._axis.steps;
		const points = steps.map(item => [this._isXDirection ? this.point(item) * width : this.point(item) * height, item]);

		return renderScale[this._position](points, this.config, width, height);
	}
	scaleGrid(): object {
		const getPoints = (width: number, height: number) =>
			this._axis.steps.map((item: number) => [this._isXDirection ? this.point(item) * width : this.point(item) * height, item]);
		const type = this._position;
		const grid = this.config.grid;
		const dashed = this.config.dashed;
		const hidden = this.config.hidden;
		const getSpecificLevel = () => this._axis.steps.indexOf(this.config.targetLine);
		const getSpecificNumber = () => this.point(this.config.targetValue);

		return {
			paint(width: number, height: number) {
				const targetLine = getSpecificLevel();
				const points = getPoints(width, height);
				const targetValue = getSpecificNumber();
				const config: IGridRenderConfig = {targetLine, dashed, grid, targetValue, hidden};
				return renderGrid[type](points, width, height, config);
			}
		};
	}
	protected _setDefaults(config: IScaleConfig) {
		const defaults = {
			scalePadding: 20,
			textPadding: 11,
			grid: true,
			targetLine: null,
			showText: true
		};
		if (config.locator) {
			this.locator = locator(config.locator);
		}
		this.config = { ...defaults, ...config };
	}
	private _logPoint(pos: number): number {
		let logPos;
		const sign = Math.abs(pos) / pos;
		const steps = this._axis.steps;
		const count = steps.length - 1;
		const index = steps.indexOf(pos);
		if (index !== -1) {
			logPos = index / count;
		} else {
			const dx = this._axis.min < 0 ? steps.indexOf(0) : 0;
			const exp = sign * (log10(Math.abs(pos)) - 1);
			logPos = (dx + exp) / count;
		}
		return this._isXDirection ?  logPos : 1 - logPos;
	}
}
